import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { AlertTriangle, AlertCircle, Info, RefreshCw, CheckCircle, Wine, Clock, Timer, CalendarX, Settings, UserX, FileWarning, Gauge } from 'lucide-react';
import MasterLinkButton from '../components/MasterLinkButton';
import * as api from '../services/api';
import { get, put, del } from '../services/api';
import { DateRangeFilter, inDateRange } from '../components/DateRangeFilter';
import { useSort, usePager, SortableTh } from '../components/TableTools';

// ─── アラート一覧 ──────────────────────────────────────────────────────────────

interface Alert {
  id: string;
  alert_type: string;
  alert_severity: string;
  alert_date: string;
  resolved_at: string | null;
  user_id: string;
  user_name: string;
  vessel_name: string;
  threshold_value: number;
  actual_value: number;
  is_resolved: boolean;
  message: string;
}

const SEV: Record<string, { bg: string; color: string; border: string; label: string; Icon: React.FC<any> }> = {
  critical: { bg: '#FEF2F2', color: '#DC2626', border: '#FECACA', label: '重大',  Icon: AlertCircle },
  warning:  { bg: '#FFFBEB', color: '#D97706', border: '#FDE68A', label: '警告',  Icon: AlertTriangle },
  info:     { bg: '#EFF6FF', color: '#2563EB', border: '#BFDBFE', label: '情報',  Icon: Info },
};
const SEV_FALLBACK = { bg: '#F9FAFB', color: '#6B7280', border: '#E5E7EB', label: '-', Icon: Info };
const SEV_RANK: Record<string, number> = { critical: 0, warning: 1, info: 2 };

const TYPE_CONFIG: Record<string, { label: string; unit: 'alcohol' | 'minutes' | 'days' | 'none'; Icon: React.FC<any> }> = {
  alcohol_detected: { label: 'アルコール検知', unit: 'alcohol',  Icon: Wine },
  overtime_exceeded:{ label: '残業超過',       unit: 'minutes',  Icon: Timer },
  consecutive_work: { label: '連続勤務',       unit: 'days',     Icon: CalendarX },
  rest_violation:   { label: '休息不足',       unit: 'minutes',  Icon: Clock },
  no_clock_out:      { label: '退勤未打刻',   unit: 'minutes',  Icon: Clock },
  missing_clock_in:    { label: '出勤未打刻',       unit: 'none',     Icon: UserX },
  license_expiring:    { label: '海技免状期限警告', unit: 'days',     Icon: FileWarning },
  seaman_book_expiring:{ label: '船員手帳期限警告', unit: 'days',     Icon: FileWarning },
  certificate_expiring:{ label: '資格・証明書の期限', unit: 'days',     Icon: FileWarning },
  sync_delay:          { label: '同期遅延',         unit: 'minutes',  Icon: RefreshCw },
  device_vessel_mismatch: { label: '船舶不一致ログイン', unit: 'none',  Icon: UserX },
  alcohol_skipped:     { label: 'アルコール未実施', unit: 'none',     Icon: Wine },
  alcohol_manual:      { label: 'アルコール手入力', unit: 'none',     Icon: Wine },
  alcohol_missing:     { label: '測定なしで勤務終了', unit: 'none',   Icon: Wine },
  alcohol_device_milestone: { label: '検知器 使用回数', unit: 'none', Icon: Gauge },
  alcohol_device_limit:     { label: '検知器 交換時期', unit: 'none', Icon: Gauge },
  face_missing:        { label: '顔写真なし',       unit: 'none',     Icon: UserX },
};

function fmtThreshold(type: string, val: number): string {
  const u = TYPE_CONFIG[type]?.unit ?? 'none';
  if (u === 'alcohol')  return `${(val / 1000).toFixed(3)} mg/L`;
  if (u === 'minutes')  return `${Math.floor(val / 60)}h${val % 60}m`;
  if (u === 'days')     return `${val}日`;
  return String(val);
}

function fmtActual(type: string, val: number): string {
  const u = TYPE_CONFIG[type]?.unit ?? 'none';
  if (u === 'alcohol')  return `${(val / 1000).toFixed(3)} mg/L`;
  if (u === 'minutes')  return `${Math.floor(val / 60)}h${val % 60}m`;
  if (u === 'days')     return `${val}日`;
  return String(val);
}

function rowBg(a: Alert, idx: number): string {
  if (!a.is_resolved && a.alert_type === 'alcohol_detected') {
    return a.alert_severity === 'critical' ? '#FFF5F5' : '#FFFDF0';
  }
  return idx % 2 === 0 ? '#fff' : '#FAFAFA';
}

// ─── 閾値設定 ──────────────────────────────────────────────────────────────────

const ALERT_TYPES: { key: string; label: string; unit: string; warnDefault: number; critDefault: number }[] = [
  { key: 'no_clock_out',      label: '業務未終了',   unit: '分',          warnDefault: 720,  critDefault: 1440 },
  { key: 'overtime_exceeded', label: '残業超過',     unit: '分',          warnDefault: 3000, critDefault: 4200 },
  { key: 'consecutive_work',  label: '連続勤務',     unit: '日',          warnDefault: 10,   critDefault: 14   },
  { key: 'sync_delay',        label: '同期遅延',     unit: '分',          warnDefault: 720,  critDefault: 1440 },
  { key: 'alcohol_warning',   label: 'アルコール',   unit: '×0.001mg/L', warnDefault: 15,   critDefault: 150  },
];

interface SettingRow {
  id: string; scope: string;
  vessel_id: string | null; user_id: string | null;
  alert_type: string; threshold_value: number;
  severity: string;
}
interface SettingsData {
  company: SettingRow[]; vessel: SettingRow[]; user: SettingRow[];
  defaults: Record<string, number>;
  critical_defaults: Record<string, number>;
}
interface Vessel { id: string; vessel_name: string; }
interface User   { id: string; user_name: string; employee_code: string; }

type SettingsTab = 'company' | 'vessel' | 'user';
type DraftKey = string;

// ─── メインコンポーネント ───────────────────────────────────────────────────────

type MainTab = 'active' | 'resolved' | 'settings';

const AlertsPage: React.FC = () => {
  const [mainTab, setMainTab] = useState<MainTab>('active');
  const [unresolvedCount, setUnresolvedCount] = useState(0);

  const tabBtn = (tab: MainTab, label: React.ReactNode) => (
    <button onClick={() => setMainTab(tab)} style={{
      display: 'flex', alignItems: 'center', gap: 6,
      padding: '10px 24px', border: 'none', background: 'none', cursor: 'pointer',
      fontSize: 14, fontWeight: mainTab === tab ? 700 : 400,
      color: mainTab === tab ? '#1e40af' : '#6B7280',
      borderBottom: mainTab === tab ? '2px solid #1e40af' : '2px solid transparent',
      marginBottom: -2,
    }}>{label}</button>
  );

  return (
    <div className="page-wrap">
      <div className="top-bar" style={{ marginBottom: 0 }}>
        <div>
          <h1 className="page-title">アラート管理</h1>
          <p style={{ margin: '3px 0 0', color: 'var(--text-secondary)', fontSize: 13 }}>
            労働時間・アルコール検知・打刻異常などのアラートを管理します
          </p>
        </div>
        <div className="flex-row">
          <MasterLinkButton tab="thresholds" title="アラートの検知閾値(勤怠閾値設定)を開きます" />
        </div>
      </div>

      <div style={{ display: 'flex', gap: 0, marginBottom: 24, marginTop: 16, borderBottom: '2px solid #E5E7EB' }}>
        {tabBtn('active', <>
          🔔 未解決
          {unresolvedCount > 0 && (
            <span style={{ background: '#ef4444', color: '#fff', borderRadius: 999, fontSize: 11, fontWeight: 800, padding: '1px 7px', minWidth: 20, textAlign: 'center' }}>
              {unresolvedCount}
            </span>
          )}
        </>)}
        {tabBtn('resolved', '✅ 確認済み')}
        {tabBtn('settings', <><Settings size={14} />閾値設定</>)}
      </div>

      {mainTab === 'active'   && <AlertList onCountChange={setUnresolvedCount} />}
      {mainTab === 'resolved' && <ResolvedAlerts />}
      {mainTab === 'settings' && <ThresholdSettings />}

      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
    </div>
  );
};

// ─── アラートデータ取得（共有フック）─────────────────────────────────────────────

/** サーバの created_at(UTC "YYYY-MM-DD HH:MM:SS") を JST の "YYYY-MM-DD HH:MM:SS" に変換 */
function utcToJst(s?: string | null): string {
  if (!s) return '';
  const hasZone = /[zZ]$|[+-]\d\d:?\d\d$/.test(s);
  const d = new Date(s.replace(' ', 'T') + (hasZone ? '' : 'Z'));
  if (isNaN(d.getTime())) return String(s);
  const j = new Date(d.getTime() + 9 * 3600 * 1000);
  const p = (n: number) => String(n).padStart(2, '0');
  return `${j.getUTCFullYear()}-${p(j.getUTCMonth() + 1)}-${p(j.getUTCDate())} ${p(j.getUTCHours())}:${p(j.getUTCMinutes())}:${p(j.getUTCSeconds())}`;
}

function useAlerts(silent = false) {
  const mapAlert = (a: any): Alert => ({
    id:              String(a.id ?? ''),
    alert_type:      String(a.alert_type ?? ''),
    // APIは severity で返す（alert_severity は旧名）。どちらも無いときだけ「情報」
    alert_severity:  String(a.alert_severity ?? a.severity ?? 'info'),
    alert_date:      utcToJst(a.alert_date ?? a.created_at ?? ''),
    resolved_at:     a.resolved_at ? utcToJst(a.resolved_at) : null,
    user_id:         String(a.user_id ?? ''),
    user_name:       String(a.user_name ?? ''),
    vessel_name:     String(a.vessel_name ?? ''),
    threshold_value: Number(a.threshold_value ?? 0),
    actual_value:    Number(a.actual_value ?? 0),
    is_resolved:     Boolean(a.is_resolved),
    message:         String(a.message ?? ''),
  });
  return mapAlert;
}

// ─── 未解決アラート一覧 ─────────────────────────────────────────────────────────

const AlertList: React.FC<{ onCountChange?: (n: number) => void }> = ({ onCountChange }) => {
  const [alerts, setAlerts]         = useState<Alert[]>([]);
  const [loading, setLoading]       = useState(true);
  const [sevFilter, setSevFilter]   = useState('');
  const [typeFilter, setTypeFilter] = useState('');
  const [dateFrom, setDateFrom]     = useState('');
  const [dateTo, setDateTo]         = useState('');
  const [lastUpdated, setLastUpdated]   = useState<Date | null>(null);
  const [selected, setSelected]     = useState<Set<string>>(new Set());
  const [bulkResolving, setBulkResolving] = useState(false);
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const mapAlert = useAlerts();
  const navigate = useNavigate();

  const goToAttendance = (a: Alert) => {
    const date = a.alert_date.slice(0, 10);
    if (a.alert_type === 'overtime_exceeded') {
      const [year, month] = date.split('-');
      navigate(`/attendance?view=timeline&uid=${a.user_id}&year=${year}&month=${Number(month)}`);
    } else {
      navigate(`/attendance?crew=${encodeURIComponent(a.user_name)}&from=${date}&to=${date}`);
    }
  };

  const fetchAlerts = async (silent = false) => {
    if (!silent) setLoading(true);
    try {
      // 未解決だけをサーバー側で絞って取得する。全件取得（最新200件）から画面で未解決を
      // 残す方式だと、最近解決した件に押し出されて古い未解決が200件の窓から漏れ、
      // サイドバーのバッジ（全未解決数）と一覧の件数が食い違う。
      const res = await api.get<any>('/alerts?is_resolved=0');
      const raw: Alert[] = (res?.alerts ?? res?.data ?? []).map(mapAlert);
      const unresolved = raw.filter(a => !a.is_resolved);
      setAlerts(unresolved);
      // 一覧から消えたアラートは選択からも外す
      setSelected(prev => {
        const next = new Set([...prev].filter(id => unresolved.some(a => a.id === id)));
        return next.size === prev.size ? prev : next;
      });
      onCountChange?.(unresolved.length);
      setLastUpdated(new Date());
    } catch (err) { console.error('[AlertsPage]', err);
      setAlerts([]);
    }
    if (!silent) setLoading(false);
  };

  useEffect(() => {
    fetchAlerts();
    intervalRef.current = setInterval(() => fetchAlerts(true), 30_000);
    return () => { if (intervalRef.current) clearInterval(intervalRef.current); };
  }, []);

  const resolve = async (id: string) => {
    try {
      await api.put('/alerts/' + id + '/resolve', {});
      setAlerts(prev => {
        const next = prev.filter(a => a.id !== id);
        onCountChange?.(next.length);
        return next;
      });
      setSelected(prev => {
        if (!prev.has(id)) return prev;
        const next = new Set(prev);
        next.delete(id);
        return next;
      });
    } catch (err) { console.error('[AlertsPage]', err); }
  };

  const resolveBulk = async () => {
    const ids = [...selected];
    if (ids.length === 0) return;
    if (!confirm(`選択した ${ids.length}件のアラートを一括で解決しますか？`)) return;
    setBulkResolving(true);
    try {
      await api.put('/alerts/resolve-bulk', { ids });
      setAlerts(prev => {
        const next = prev.filter(a => !selected.has(a.id));
        onCountChange?.(next.length);
        return next;
      });
      setSelected(new Set());
    } catch (err) {
      console.error('[AlertsPage]', err);
      alert(`一括解決に失敗しました。${err instanceof Error ? err.message : ''}`);
    }
    setBulkResolving(false);
  };

  const toggleSelect = (id: string) => setSelected(prev => {
    const next = new Set(prev);
    if (next.has(id)) next.delete(id); else next.add(id);
    return next;
  });

  const filtered = alerts.filter(a => {
    if (sevFilter  && a.alert_severity !== sevFilter)  return false;
    if (typeFilter && a.alert_type     !== typeFilter)  return false;
    if (!inDateRange(a.alert_date, dateFrom, dateTo))   return false;
    return true;
  });

  // ソート → ページング（共通部品）
  const sort = useSort<Alert>(filtered, {
    severity: a => SEV_RANK[a.alert_severity] ?? 9,
    type:     a => TYPE_CONFIG[a.alert_type]?.label ?? a.alert_type,
    user:     a => a.user_name,
    vessel:   a => a.vessel_name,
    date:     a => a.alert_date,
    actual:   a => a.actual_value,
  }, 'date', 'desc');
  const pager = usePager(sort.sorted, { noun: '件' });

  // 全選択は「今表示しているページ分」だけを対象にする（例: 20件表示なら20件のみ）
  const allPageSelected = pager.pageRows.length > 0 && pager.pageRows.every(a => selected.has(a.id));

  const toggleSelectAll = () => setSelected(prev => {
    const next = new Set(prev);
    if (allPageSelected) pager.pageRows.forEach(a => next.delete(a.id));
    else pager.pageRows.forEach(a => next.add(a.id));
    return next;
  });

  const counts = {
    critical: alerts.filter(a => a.alert_severity === 'critical').length,
    warning:  alerts.filter(a => a.alert_severity === 'warning').length,
    info:     alerts.filter(a => a.alert_severity === 'info').length,
  };

  const alcoholUnresolved = alerts.filter(a => a.alert_type === 'alcohol_detected');

  return (
    <>
      <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 16, gap: 10, alignItems: 'center' }}>
        {lastUpdated && (
          <span style={{ fontSize: 11, color: '#9CA3AF' }}>
            最終更新: {lastUpdated.toLocaleTimeString('ja-JP', { hour: '2-digit', minute: '2-digit', second: '2-digit' })}
          </span>
        )}
        <button onClick={() => fetchAlerts()} className="btn btn-secondary btn-sm" style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
          <RefreshCw size={13} />更新
        </button>
      </div>

      {alcoholUnresolved.length > 0 && (
        <div style={{
          background: alcoholUnresolved.some(a => a.alert_severity === 'critical') ? '#FEF2F2' : '#FFFBEB',
          border: `1.5px solid ${alcoholUnresolved.some(a => a.alert_severity === 'critical') ? '#FECACA' : '#FDE68A'}`,
          borderRadius: 10, padding: '14px 20px', marginBottom: 20,
          display: 'flex', alignItems: 'flex-start', gap: 14,
        }}>
          <Wine size={22} color={alcoholUnresolved.some(a => a.alert_severity === 'critical') ? '#DC2626' : '#D97706'} style={{ flexShrink: 0, marginTop: 1 }} />
          <div>
            <div style={{ fontWeight: 700, fontSize: 15, color: alcoholUnresolved.some(a => a.alert_severity === 'critical') ? '#DC2626' : '#D97706', marginBottom: 6 }}>
              {alcoholUnresolved.some(a => a.alert_severity === 'critical')
                ? `法定基準値超過のアルコールが ${alcoholUnresolved.filter(a => a.alert_severity === 'critical').length}件 検知されています`
                : `アルコール検知アラートが ${alcoholUnresolved.length}件 あります（要上長確認）`}
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
              {alcoholUnresolved.map(a => (
                <div key={a.id} style={{ fontSize: 13, color: '#374151', display: 'flex', alignItems: 'center', gap: 8 }}>
                  <span style={{ fontWeight: 600 }}>{a.user_name}</span>
                  <span style={{ color: '#6B7280' }}>({a.vessel_name || '船舶不明'})</span>
                  <span style={{ color: a.alert_severity === 'critical' ? '#DC2626' : '#D97706', fontWeight: 700 }}>
                    {fmtActual('alcohol_detected', a.actual_value)}
                  </span>
                  <span style={{ color: '#9CA3AF', fontSize: 11 }}>{a.alert_date.slice(0, 16)}</span>
                  <button onClick={() => resolve(a.id)}
                    style={{ padding: '2px 10px', background: '#22C55E', color: '#fff', border: 'none', borderRadius: 5, cursor: 'pointer', fontSize: 11, fontWeight: 600 }}>
                    解決
                  </button>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3,1fr)', gap: 16, marginBottom: 24 }}>
        {(Object.entries(SEV) as [string, typeof SEV[string]][]).map(([key, cfg]) => {
          const isActive = sevFilter === key;
          return (
            <div key={key}
              onClick={() => setSevFilter(isActive ? '' : key)}
              style={{
                background: '#fff', borderRadius: 10, padding: '16px 20px',
                boxShadow: '0 2px 8px rgba(0,0,0,0.06)',
                borderLeft: `4px solid ${cfg.color}`,
                outline: isActive ? `2px solid ${cfg.color}` : 'none',
                cursor: 'pointer', transition: 'box-shadow 0.15s',
              }}
              onMouseEnter={e => e.currentTarget.style.boxShadow = '0 4px 16px rgba(0,0,0,0.10)'}
              onMouseLeave={e => e.currentTarget.style.boxShadow = '0 2px 8px rgba(0,0,0,0.06)'}
            >
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 4 }}>
                <div style={{ fontSize: 13, color: '#6B7280', fontWeight: 600 }}>{cfg.label}</div>
                <cfg.Icon size={16} color={cfg.color} />
              </div>
              <div style={{ fontSize: 32, fontWeight: 800, color: cfg.color }}>
                {counts[key as keyof typeof counts] ?? 0}
              </div>
              <div style={{ fontSize: 11, color: '#9CA3AF', marginTop: 4 }}>未解決件数</div>
            </div>
          );
        })}
      </div>

      <div style={{ background: '#fff', borderRadius: 10, padding: '14px 20px', marginBottom: 20,
        boxShadow: '0 2px 8px rgba(0,0,0,0.06)', display: 'flex', gap: 16, flexWrap: 'wrap', alignItems: 'center' }}>
        <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13, color: '#374151' }}>
          種別：
          <select value={typeFilter} onChange={e => setTypeFilter(e.target.value)}
            style={{ border: '1px solid #E5E7EB', borderRadius: 6, padding: '5px 10px', fontSize: 13, outline: 'none', background: '#fff' }}>
            <option value="">全種別</option>
            {Object.entries(TYPE_CONFIG).map(([k, v]) => (
              <option key={k} value={k}>{v.label}</option>
            ))}
          </select>
        </label>
        <DateRangeFilter from={dateFrom} to={dateTo} onFrom={setDateFrom} onTo={setDateTo} />
        {selected.size > 0 && (
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <span style={{ fontSize: 13, fontWeight: 700, color: '#1e40af' }}>{selected.size}件選択中</span>
            <button onClick={resolveBulk} disabled={bulkResolving}
              style={{ display: 'inline-flex', alignItems: 'center', gap: 5, padding: '6px 16px', background: '#22C55E', color: '#fff', border: 'none', borderRadius: 6, cursor: 'pointer', fontSize: 12, fontWeight: 700, opacity: bulkResolving ? 0.6 : 1 }}>
              <CheckCircle size={13} />{bulkResolving ? '処理中...' : '選択したアラートを解決'}
            </button>
            <button onClick={() => setSelected(new Set())} disabled={bulkResolving}
              style={{ padding: '6px 12px', background: '#F1F5F9', color: '#64748b', border: 'none', borderRadius: 6, cursor: 'pointer', fontSize: 12, fontWeight: 600 }}>
              選択解除
            </button>
          </div>
        )}
        <span style={{ marginLeft: 'auto', color: '#9CA3AF', fontSize: 12 }}>{filtered.length}件表示 / 30秒ごとに自動更新</span>
      </div>

      {!loading && filtered.length > 0 && pager.renderBar('top')}
      <div style={{ background: '#fff', borderRadius: 10, boxShadow: '0 2px 8px rgba(0,0,0,0.06)', overflow: 'hidden' }}>
        {loading ? (
          <div style={{ textAlign: 'center', padding: 48, color: '#94a3b8', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 10 }}>
            <RefreshCw size={16} style={{ animation: 'spin 1s linear infinite' }} />読み込み中...
          </div>
        ) : filtered.length === 0 ? (
          <div style={{ textAlign: 'center', padding: 48, color: '#94a3b8' }}>
            <CheckCircle size={36} style={{ margin: '0 auto 12px', display: 'block', color: '#22C55E' }} />
            未解決のアラートはありません
          </div>
        ) : (
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
            <thead>
              <tr style={{ background: '#F8FAFC' }}>
                <th style={{ padding: '12px 14px', width: 36, borderBottom: '1px solid #E5E7EB' }}>
                  <input type="checkbox" checked={allPageSelected} onChange={toggleSelectAll}
                    title="このページに表示中のアラートを全選択/解除"
                    style={{ width: 15, height: 15, cursor: 'pointer', accentColor: '#1e40af' }} />
                </th>
                <SortableTh label="重要度" k="severity" sort={sort} />
                <SortableTh label="種別" k="type" sort={sort} />
                <SortableTh label="乗組員" k="user" sort={sort} />
                <SortableTh label="船舶" k="vessel" sort={sort} />
                <SortableTh label="日時" k="date" sort={sort} />
                {['詳細', '閾値'].map(h => (
                  <th key={h} style={{ padding: '12px 14px', textAlign: 'left', color: '#6B7280', fontWeight: 600, fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.05em', borderBottom: '1px solid #E5E7EB', whiteSpace: 'nowrap' }}>{h}</th>
                ))}
                <SortableTh label="実測値" k="actual" sort={sort} />
                {['状態', '操作'].map(h => (
                  <th key={h} style={{ padding: '12px 14px', textAlign: 'left', color: '#6B7280', fontWeight: 600, fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.05em', borderBottom: '1px solid #E5E7EB', whiteSpace: 'nowrap' }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {pager.pageRows.map((a, i) => {
                const sev  = SEV[a.alert_severity] ?? SEV_FALLBACK;
                const type = TYPE_CONFIG[a.alert_type];
                const TypeIcon = type?.Icon ?? Info;
                return (
                  <tr key={a.id} style={{ background: selected.has(a.id) ? '#EFF6FF' : rowBg(a, i), borderBottom: '1px solid #F3F4F6', opacity: a.is_resolved ? 0.55 : 1, transition: 'opacity 0.2s, background 0.15s' }}>
                    <td style={{ padding: '12px 14px' }}>
                      <input type="checkbox" checked={selected.has(a.id)} onChange={() => toggleSelect(a.id)}
                        style={{ width: 15, height: 15, cursor: 'pointer', accentColor: '#1e40af' }} />
                    </td>
                    <td style={{ padding: '12px 14px' }}>
                      <span style={{
                        display: 'inline-flex', alignItems: 'center', gap: 5,
                        background: sev.bg, color: sev.color,
                        border: `1px solid ${sev.border}`,
                        borderRadius: 20, padding: '3px 10px', fontSize: 11, fontWeight: 700,
                      }}>
                        <sev.Icon size={11} />{sev.label}
                      </span>
                    </td>
                    <td style={{ padding: '12px 14px' }}>
                      <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5, fontSize: 12, fontWeight: 600, color: a.alert_type === 'alcohol_detected' ? (a.alert_severity === 'critical' ? '#DC2626' : '#D97706') : '#374151' }}>
                        <TypeIcon size={13} />{type?.label ?? a.alert_type}
                      </span>
                    </td>
                    <td style={{ padding: '12px 14px' }}>
                      <button onClick={() => goToAttendance(a)} style={{
                        background: 'none', border: 'none', padding: 0, cursor: 'pointer',
                        fontWeight: 600, color: '#1d4ed8', fontSize: 13, textDecoration: 'underline',
                        textDecorationStyle: 'dotted', textUnderlineOffset: 3,
                      }} title="勤怠を確認">
                        {a.user_name}
                      </button>
                    </td>
                    <td style={{ padding: '12px 14px', color: '#6B7280', fontSize: 12 }}>{a.vessel_name || '-'}</td>
                    <td style={{ padding: '12px 14px', fontSize: 11, color: '#9CA3AF', whiteSpace: 'nowrap' }}>
                      {a.alert_date.slice(0, 16).replace('T', ' ')}
                    </td>
                    <td style={{ padding: '12px 14px', fontSize: 12, color: '#374151', maxWidth: 240 }}>
                      <span title={a.message} style={{ display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>
                        {a.message || '-'}
                      </span>
                    </td>
                    <td style={{ padding: '12px 14px', fontSize: 12, color: '#6B7280', whiteSpace: 'nowrap' }}>
                      {fmtThreshold(a.alert_type, a.threshold_value)}
                    </td>
                    <td style={{ padding: '12px 14px', whiteSpace: 'nowrap' }}>
                      <span style={{ fontSize: 13, fontWeight: 700, color: a.alert_severity === 'critical' ? '#DC2626' : '#D97706' }}>
                        {fmtActual(a.alert_type, a.actual_value)}
                      </span>
                    </td>
                    <td style={{ padding: '12px 14px' }}>
                      {a.is_resolved
                        ? <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, color: '#22C55E', fontSize: 12, fontWeight: 600 }}>
                            <CheckCircle size={13} />解決済
                          </span>
                        : <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, color: '#EF4444', fontSize: 12, fontWeight: 600 }}>
                            <AlertCircle size={13} />未解決
                          </span>}
                    </td>
                    <td style={{ padding: '12px 14px' }}>
                      {!a.is_resolved && (
                        <button onClick={() => resolve(a.id)}
                          style={{ padding: '5px 14px', background: '#22C55E', color: '#fff', border: 'none', borderRadius: 6, cursor: 'pointer', fontSize: 12, fontWeight: 600, transition: 'background 0.15s' }}
                          onMouseEnter={e => e.currentTarget.style.background = '#16A34A'}
                          onMouseLeave={e => e.currentTarget.style.background = '#22C55E'}>
                          解決
                        </button>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>
      {!loading && filtered.length > 0 && pager.renderBar('bottom')}
    </>
  );
};

// ─── 確認済みアラート一覧 ───────────────────────────────────────────────────────

const ResolvedAlerts: React.FC = () => {
  const [alerts, setAlerts]         = useState<Alert[]>([]);
  const [loading, setLoading]       = useState(true);
  const [typeFilter, setTypeFilter] = useState('');
  const [dateFrom, setDateFrom]     = useState('');
  const [dateTo, setDateTo]         = useState('');
  const mapAlert = useAlerts();

  useEffect(() => {
    (async () => {
      try {
        // 確認済みタブも解決済みだけをサーバー側で絞る（未解決と混ざると最新200件の窓が狭まる）
        const res = await api.get<any>('/alerts?is_resolved=1');
        const raw: Alert[] = (res?.alerts ?? res?.data ?? []).map(mapAlert);
        setAlerts(raw.filter(a => a.is_resolved));
      } catch (err) { console.error('[AlertsPage]', err);
        setAlerts([]);
      }
      setLoading(false);
    })();
  }, []);

  const filtered = alerts.filter(a => {
    if (typeFilter && a.alert_type !== typeFilter) return false;
    if (!inDateRange(a.resolved_at ?? a.alert_date, dateFrom, dateTo)) return false;
    return true;
  });

  // ソート → ページング（未解決タブと同じ共通部品。既定は確認日時の新しい順）
  const sort = useSort<Alert>(filtered, {
    severity: a => SEV_RANK[a.alert_severity] ?? 9,
    type:     a => TYPE_CONFIG[a.alert_type]?.label ?? a.alert_type,
    user:     a => a.user_name,
    vessel:   a => a.vessel_name,
    date:     a => a.alert_date,
    resolved: a => a.resolved_at ?? '',
    actual:   a => a.actual_value,
  }, 'resolved', 'desc');
  const pager = usePager(sort.sorted, { noun: '件' });

  if (loading) return (
    <div style={{ padding: 48, textAlign: 'center', color: '#94a3b8', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 10 }}>
      <RefreshCw size={16} style={{ animation: 'spin 1s linear infinite' }} />読み込み中...
    </div>
  );

  return (
    <>
      <div style={{ background: '#fff', borderRadius: 10, padding: '14px 20px', marginBottom: 20,
        boxShadow: '0 2px 8px rgba(0,0,0,0.06)', display: 'flex', gap: 16, flexWrap: 'wrap', alignItems: 'center' }}>
        <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13, color: '#374151' }}>
          種別：
          <select value={typeFilter} onChange={e => setTypeFilter(e.target.value)}
            style={{ border: '1px solid #E5E7EB', borderRadius: 6, padding: '5px 10px', fontSize: 13, outline: 'none', background: '#fff' }}>
            <option value="">全種別</option>
            {Object.entries(TYPE_CONFIG).map(([k, v]) => (
              <option key={k} value={k}>{v.label}</option>
            ))}
          </select>
        </label>
        <DateRangeFilter from={dateFrom} to={dateTo} onFrom={setDateFrom} onTo={setDateTo} />
        <span style={{ marginLeft: 'auto', color: '#9CA3AF', fontSize: 12 }}>{filtered.length}件</span>
      </div>

      {filtered.length > 0 && pager.renderBar('top')}

      {filtered.length === 0 ? (
        <div style={{ textAlign: 'center', padding: 48, color: '#94a3b8', background: '#fff', borderRadius: 10, boxShadow: '0 2px 8px rgba(0,0,0,0.06)' }}>
          <CheckCircle size={36} style={{ margin: '0 auto 12px', display: 'block', color: '#22C55E' }} />
          確認済みのアラートはありません
        </div>
      ) : (
        <div style={{ background: '#fff', borderRadius: 10, boxShadow: '0 2px 8px rgba(0,0,0,0.06)', overflow: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
            <thead>
              <tr style={{ background: '#F8FAFC' }}>
                <SortableTh label="重要度" k="severity" sort={sort} />
                <SortableTh label="種別" k="type" sort={sort} />
                <SortableTh label="乗組員" k="user" sort={sort} />
                <SortableTh label="船舶" k="vessel" sort={sort} />
                <SortableTh label="発生日時" k="date" sort={sort} />
                <SortableTh label="確認日時" k="resolved" sort={sort} />
                <th style={{ padding: '10px 14px', textAlign: 'left', color: '#6B7280', fontWeight: 600, fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.05em', borderBottom: '1px solid #E5E7EB', whiteSpace: 'nowrap' }}>詳細</th>
                <SortableTh label="実測値" k="actual" sort={sort} />
              </tr>
            </thead>
            <tbody>
                {pager.pageRows.map((a, i) => {
                  const sev  = SEV[a.alert_severity] ?? SEV_FALLBACK;
                  const type = TYPE_CONFIG[a.alert_type];
                  const TypeIcon = type?.Icon ?? Info;
                  return (
                    <tr key={a.id} style={{ background: i % 2 === 0 ? '#fff' : '#FAFAFA', borderBottom: '1px solid #F3F4F6', opacity: 0.75 }}>
                      <td style={{ padding: '10px 14px' }}>
                        <span style={{
                          display: 'inline-flex', alignItems: 'center', gap: 5,
                          background: sev.bg, color: sev.color,
                          border: `1px solid ${sev.border}`,
                          borderRadius: 20, padding: '3px 10px', fontSize: 11, fontWeight: 700,
                        }}>
                          <sev.Icon size={11} />{sev.label}
                        </span>
                      </td>
                      <td style={{ padding: '10px 14px' }}>
                        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5, fontSize: 12, fontWeight: 600, color: '#374151' }}>
                          <TypeIcon size={13} />{type?.label ?? a.alert_type}
                        </span>
                      </td>
                      <td style={{ padding: '10px 14px', fontWeight: 600, color: '#374151' }}>{a.user_name}</td>
                      <td style={{ padding: '10px 14px', color: '#6B7280', fontSize: 12 }}>{a.vessel_name || '-'}</td>
                      <td style={{ padding: '10px 14px', fontSize: 11, color: '#9CA3AF', whiteSpace: 'nowrap' }}>
                        {a.alert_date.slice(0, 16).replace('T', ' ')}
                      </td>
                      <td style={{ padding: '10px 14px', fontSize: 11, color: '#22C55E', fontWeight: 600, whiteSpace: 'nowrap' }}>
                        {a.resolved_at ? a.resolved_at.slice(0, 16).replace('T', ' ') : '-'}
                      </td>
                      <td style={{ padding: '10px 14px', fontSize: 12, color: '#374151', maxWidth: 220 }}>
                        <span title={a.message} style={{ display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>
                          {a.message || '-'}
                        </span>
                      </td>
                      <td style={{ padding: '10px 14px', whiteSpace: 'nowrap', fontSize: 12, color: '#6B7280' }}>
                        {fmtActual(a.alert_type, a.actual_value)}
                      </td>
                    </tr>
                  );
                })}
            </tbody>
          </table>
        </div>
      )}

      {filtered.length > 0 && pager.renderBar('bottom')}
    </>
  );
};

// ─── 閾値設定パネル ─────────────────────────────────────────────────────────────

const ThresholdSettings: React.FC = () => {
  const [tab, setTab]           = useState<SettingsTab>('company');
  const [settings, setSettings] = useState<SettingsData | null>(null);
  const [vessels, setVessels]   = useState<Vessel[]>([]);
  const [users, setUsers]       = useState<User[]>([]);
  const [loading, setLoading]   = useState(true);
  const [draft, setDraft]       = useState<Record<DraftKey, string>>({});
  const [saving, setSaving]     = useState<DraftKey | null>(null);
  const [toast, setToast]       = useState<{ msg: string; ok: boolean } | null>(null);
  const [selectedUserId, setSelectedUserId] = useState('');

  const showToast = (msg: string, ok = true) => {
    setToast({ msg, ok });
    setTimeout(() => setToast(null), 2500);
  };

  const buildDraft = useCallback((data: SettingsData): Record<DraftKey, string> => {
    const d: Record<DraftKey, string> = {};
    ALERT_TYPES.forEach(t => {
      const cw = data.company.find(s => s.alert_type === t.key && s.severity === 'warning');
      const cc = data.company.find(s => s.alert_type === t.key && s.severity === 'critical');
      d[`company-${t.key}-warning`]  = cw ? String(cw.threshold_value) : String(t.warnDefault);
      d[`company-${t.key}-critical`] = cc ? String(cc.threshold_value) : String(t.critDefault);
    });
    data.vessel.forEach(s => { d[`vessel-${s.vessel_id}-${s.alert_type}-${s.severity}`] = String(s.threshold_value); });
    data.user.forEach(s   => { d[`user-${s.user_id}-${s.alert_type}-${s.severity}`]     = String(s.threshold_value); });
    return d;
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [sd, vd, ud] = await Promise.all([
        get<{ data: SettingsData }>('/alert-settings'),
        get<{ data: Vessel[] }>('/vessels'),
        get<{ data: User[] }>('/users'),
      ]);
      setSettings(sd.data);
      setVessels(vd.data);
      setUsers(ud.data);
      setDraft(buildDraft(sd.data));
    } catch (err) { console.error('[AlertsPage]', err); }
    finally { setLoading(false); }
  }, [buildDraft]);

  useEffect(() => { load(); }, [load]);

  // vessel tab の draft 初期化（JSX の map 内で setDraft を呼ぶと無限レンダリングになるため useEffect で処理）
  useEffect(() => {
    if (!settings) return;
    setDraft(d => {
      const next = { ...d };
      let changed = false;
      vessels.forEach(v => {
        ALERT_TYPES.forEach(t => {
          const warnDK = `vessel-${v.id}-${t.key}-warning`;
          const critDK = `vessel-${v.id}-${t.key}-critical`;
          if (!(warnDK in next)) {
            const cs = settings.company.find(s => s.alert_type === t.key && s.severity === 'warning');
            next[warnDK] = cs ? String(cs.threshold_value) : String(t.warnDefault);
            const cc = settings.company.find(s => s.alert_type === t.key && s.severity === 'critical');
            next[critDK] = cc ? String(cc.threshold_value) : String(t.critDefault);
            changed = true;
          }
        });
      });
      return changed ? next : d;
    });
  }, [vessels, settings]);

  // user tab の draft 初期化
  useEffect(() => {
    if (!settings || !selectedUserId) return;
    setDraft(d => {
      const next = { ...d };
      let changed = false;
      ALERT_TYPES.forEach(t => {
        const warnDK = `user-${selectedUserId}-${t.key}-warning`;
        const critDK = `user-${selectedUserId}-${t.key}-critical`;
        if (!(warnDK in next)) {
          const cs = settings.company.find(s => s.alert_type === t.key && s.severity === 'warning');
          next[warnDK] = cs ? String(cs.threshold_value) : String(t.warnDefault);
          const cc = settings.company.find(s => s.alert_type === t.key && s.severity === 'critical');
          next[critDK] = cc ? String(cc.threshold_value) : String(t.critDefault);
          changed = true;
        }
      });
      return changed ? next : d;
    });
  }, [selectedUserId, settings]);

  const setVal = (key: DraftKey, val: string) => setDraft(d => ({ ...d, [key]: val }));

  const getEffectiveVal = (alertType: string, severity: 'warning' | 'critical'): string => {
    const t = ALERT_TYPES.find(x => x.key === alertType);
    const defVal = severity === 'critical' ? (t?.critDefault ?? 0) : (t?.warnDefault ?? 0);
    if (!settings) return String(defVal);
    const cs = settings.company.find(s => s.alert_type === alertType && s.severity === severity);
    return cs ? String(cs.threshold_value) : String(defVal);
  };

  const save = async (draftKey: DraftKey, body: object) => {
    const val = parseInt(draft[draftKey] ?? '0', 10);
    if (isNaN(val) || val <= 0) { showToast('1以上の値を入力してください', false); return; }
    setSaving(draftKey);
    try {
      await put('/alert-settings', { ...body, threshold_value: val });
      await load();
      showToast('保存しました');
    } catch { showToast('保存に失敗しました', false); }
    finally { setSaving(null); }
  };

  const remove = async (id: string) => {
    if (!confirm('この設定を削除し、上位の値に戻しますか？')) return;
    try {
      await del(`/alert-settings/${id}`);
      await load();
      showToast('削除しました');
    } catch { showToast('削除に失敗しました', false); }
  };

  // 警告・重大の2列を1行で表示
  const renderAlertRow = (
    alertType: string,
    warnDraftKey: DraftKey, critDraftKey: DraftKey,
    warnId: string | undefined, critId: string | undefined,
    saveBody: Omit<object, 'severity'>,
  ) => {
    const t = ALERT_TYPES.find(x => x.key === alertType)!;
    const warnVal = draft[warnDraftKey] ?? String(t.warnDefault);
    const critVal = draft[critDraftKey] ?? String(t.critDefault);
    const savingWarn = saving === warnDraftKey;
    const savingCrit = saving === critDraftKey;

    const cell = (
      sev: 'warning' | 'critical',
      draftKey: DraftKey,
      val: string,
      existingId: string | undefined,
      isSaving: boolean,
    ) => (
      <div style={{
        display: 'flex', alignItems: 'center', gap: 6, flex: 1,
        padding: '8px 10px', borderRadius: 8,
        background: existingId ? (sev === 'warning' ? '#fffbeb' : '#fef2f2') : '#f8fafc',
        border: `1px solid ${existingId ? (sev === 'warning' ? '#fde68a' : '#fecaca') : '#e2e8f0'}`,
      }}>
        <span style={{
          fontSize: 10, fontWeight: 700, padding: '2px 6px', borderRadius: 20, whiteSpace: 'nowrap',
          background: sev === 'warning' ? '#FEF3C7' : '#FEE2E2',
          color: sev === 'warning' ? '#D97706' : '#DC2626',
        }}>{sev === 'warning' ? '警告' : '重大'}</span>
        <input type="number" value={val} min={1}
          onChange={e => setVal(draftKey, e.target.value)}
          style={{ width: 70, padding: '5px 6px', borderRadius: 6, border: '1.5px solid #d1d5db', fontSize: 13, textAlign: 'right' }}
        />
        <span style={{ fontSize: 10, color: '#64748b', flexShrink: 0 }}>{t.unit}</span>
        <button onClick={() => save(draftKey, { ...saveBody, severity: sev })} disabled={isSaving}
          style={{ padding: '4px 10px', background: sev === 'warning' ? '#D97706' : '#DC2626', color: 'white', border: 'none', borderRadius: 6, fontSize: 11, fontWeight: 600, cursor: 'pointer', opacity: isSaving ? 0.6 : 1, flexShrink: 0 }}>
          {isSaving ? '...' : '保存'}
        </button>
        {existingId && (
          <button onClick={() => remove(existingId)} title="上位の値に戻す"
            style={{ padding: '4px 7px', background: '#f1f5f9', color: '#64748b', border: 'none', borderRadius: 6, fontSize: 11, cursor: 'pointer' }}>
            ↩
          </button>
        )}
      </div>
    );

    return (
      <div key={alertType} style={{ display: 'flex', flexDirection: 'column', gap: 6, padding: '10px 14px', background: '#fff', borderRadius: 10, border: '1px solid #e2e8f0' }}>
        <span style={{ fontSize: 13, fontWeight: 700, color: '#1e293b' }}>{t.label}</span>
        <div style={{ display: 'flex', gap: 8 }}>
          {cell('warning',  warnDraftKey, warnVal, warnId, savingWarn)}
          {cell('critical', critDraftKey, critVal, critId, savingCrit)}
        </div>
      </div>
    );
  };

  if (loading) return <div style={{ padding: 40, textAlign: 'center', color: '#94a3b8' }}>読み込み中...</div>;
  if (!settings) return null;

  return (
    <>
      {toast && (
        <div style={{
          position: 'fixed', top: 20, left: '50%', transform: 'translateX(-50%)',
          background: toast.ok ? '#0f172a' : '#dc2626', color: 'white',
          padding: '10px 24px', borderRadius: 999, fontSize: 14, fontWeight: 700,
          zIndex: 100, boxShadow: '0 4px 16px rgba(0,0,0,0.2)',
        }}>{toast.msg}</div>
      )}

      <div style={{ background: '#eff6ff', border: '1px solid #bfdbfe', borderRadius: 8, padding: '10px 14px', marginBottom: 20, fontSize: 13, color: '#1e40af' }}>
        優先順位: <strong>船員個別</strong> &gt; <strong>船別</strong> &gt; <strong>会社全体</strong> &gt; デフォルト値　　↩ボタンで上位の値に戻します
      </div>

      <div style={{ display: 'flex', gap: 4, marginBottom: 20, background: '#f1f5f9', borderRadius: 10, padding: 4 }}>
        {([['company','🏢 会社全体'],['vessel','🚢 船別'],['user','👤 船員別']] as [SettingsTab,string][]).map(([t, l]) => (
          <button key={t} onClick={() => setTab(t)} style={{
            flex: 1, padding: '10px 0', border: 'none', borderRadius: 8,
            background: tab === t ? 'white' : 'transparent',
            fontWeight: tab === t ? 700 : 500, fontSize: 14,
            color: tab === t ? '#1e40af' : '#64748b', cursor: 'pointer',
            boxShadow: tab === t ? '0 1px 4px rgba(0,0,0,0.1)' : 'none',
          }}>{l}</button>
        ))}
      </div>

      {tab === 'company' && (
        <div className="card">
          <div className="card-h">会社全体のデフォルト値</div>
          <p style={{ fontSize: 12, color: '#94a3b8', marginBottom: 16 }}>
            警告と重大それぞれの閾値を設定します。船別・船員別に個別設定がない場合はこの値が使用されます
          </p>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            {ALERT_TYPES.map(t => {
              const warnId = settings.company.find(s => s.alert_type === t.key && s.severity === 'warning')?.id;
              const critId = settings.company.find(s => s.alert_type === t.key && s.severity === 'critical')?.id;
              return renderAlertRow(
                t.key,
                `company-${t.key}-warning`,
                `company-${t.key}-critical`,
                warnId, critId,
                { scope: 'company', alert_type: t.key }
              );
            })}
          </div>
        </div>
      )}

      {tab === 'vessel' && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
          {vessels.length === 0 && <div style={{ textAlign: 'center', color: '#94a3b8', padding: 32 }}>船舶が登録されていません</div>}
          {vessels.map(v => (
            <div key={v.id} className="card">
              <div className="card-h">🚢 {v.vessel_name}</div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                {ALERT_TYPES.map(t => {
                  const warnDK = `vessel-${v.id}-${t.key}-warning`;
                  const critDK = `vessel-${v.id}-${t.key}-critical`;
                  const warnId = settings.vessel.find(s => s.vessel_id === v.id && s.alert_type === t.key && s.severity === 'warning')?.id;
                  const critId = settings.vessel.find(s => s.vessel_id === v.id && s.alert_type === t.key && s.severity === 'critical')?.id;
                  return renderAlertRow(t.key, warnDK, critDK, warnId, critId, { scope: 'vessel', vessel_id: v.id, alert_type: t.key });
                })}
              </div>
            </div>
          ))}
        </div>
      )}

      {tab === 'user' && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
          <div className="card">
            <div className="card-h">船員を選択</div>
            <select value={selectedUserId} onChange={e => setSelectedUserId(e.target.value)}
              style={{ width: '100%', padding: '10px 12px', border: '1.5px solid #d1d5db', borderRadius: 8, fontSize: 14 }}>
              <option value="">船員を選択してください</option>
              {users.map(u => (
                <option key={u.id} value={u.id}>{u.user_name}（{u.employee_code}）</option>
              ))}
            </select>
          </div>
          {selectedUserId && (() => {
            const u = users.find(x => x.id === selectedUserId);
            return (
              <div className="card">
                <div className="card-h">👤 {u?.user_name} の個別設定</div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                  {ALERT_TYPES.map(t => {
                    const warnDK = `user-${selectedUserId}-${t.key}-warning`;
                    const critDK = `user-${selectedUserId}-${t.key}-critical`;
                    const warnId = settings.user.find(s => s.user_id === selectedUserId && s.alert_type === t.key && s.severity === 'warning')?.id;
                    const critId = settings.user.find(s => s.user_id === selectedUserId && s.alert_type === t.key && s.severity === 'critical')?.id;
                    return renderAlertRow(t.key, warnDK, critDK, warnId, critId, { scope: 'user', user_id: selectedUserId, alert_type: t.key });
                  })}
                </div>
              </div>
            );
          })()}
        </div>
      )}
    </>
  );
};

export default AlertsPage;
